import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Typography,
  IconButton,
  Modal,
  ModalDialog,
} from "@mui/joy";
import Grid from "@mui/joy/Grid";
import { useTheme } from "@mui/joy/styles";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import DropZone from "../components/common/DropZone";
import FileUpload from "../components/common/FileUpload";
import { useSnackbarStore } from "../store/useSnackbarStore";
import dayjs, { Dayjs } from "dayjs";
import { Link } from "react-router-dom";
import JoyDatePicker from "../components/common/JoyDatePicker";
import { getSevaGradientSurface } from "../theme/branding";
import {
  createAdminDarshan,
  DarshanMedia,
  DarshanMediaCreate,
  DarshanType,
  deleteAdminDarshan,
  fetchAdminDarshan,
  updateAdminDarshan,
  uploadAdminMedia,
} from "../api";

const DARSHAN_TYPES: DarshanType[] = ["Sringar", "Mangala", "Festival"];

export default function AdminDarshanPage() {
  interface QueueItem {
    id: string;
    file: File;
    name: string;
    size: number;
    mediaType: "image" | "video";
    progress: number;
    url?: string;
    title: string;
    created?: boolean;
    error?: string;
    preview?: string;
    uploading?: boolean;
    creating?: boolean;
  }
  const [type, setType] = useState<DarshanType>("Sringar");
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [addDate, setAddDate] = useState<Dayjs>(dayjs());
  const [media, setMedia] = useState<DarshanMedia[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<{
    mediaType: "image" | "video";
    url: string;
    title?: string;
  }>({ mediaType: "image", url: "", title: "" });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const showSnackbar = useSnackbarStore((s) => s.showSnackbar);
  const [queueDragIndex, setQueueDragIndex] = useState<number | null>(null);
  const [queueDragOverIndex, setQueueDragOverIndex] = useState<number | null>(null);
  const [queueDragOverPos, setQueueDragOverPos] = useState<"above" | "below" | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number>(0);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const heroBackground = isDark
    ? "linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)"
    : "radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)";
  const heroOverlay = isDark
    ? "radial-gradient(120% 120% at 15% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)"
    : "radial-gradient(120% 120% at 15% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)";
  const heroTitle = isDark ? "rgba(248,250,252,0.94)" : "text.primary";
  const heroBody = isDark ? "rgba(226,232,240,0.8)" : "text.secondary";
  const panelWarm = isDark
    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))"
    : "linear-gradient(135deg, rgba(255, 237, 213, 0.9), rgba(255, 247, 231, 0.9))";
  const panelCool = isDark
    ? "linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))"
    : "linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))";
  const gradient = getSevaGradientSurface(theme, { withShadow: true });
  const isoDateDisplay = useMemo(() => date.format("YYYY-MM-DD"), [date]);
  // Keep API query on YYYY-MM-DD while POSTs send full RFC3339 timestamp
  const isoDateQuery = isoDateDisplay;
  const isoAddDate = useMemo(() => addDate.format("YYYY-MM-DD"), [addDate]);
  const isoAddDateUtc = useMemo(() => addDate.startOf("day").toISOString(), [addDate]);
  const queueReadyCount = useMemo(
    () => queue.filter((item) => item.url && !item.created).length,
    [queue],
  );
  const sortedMedia = useMemo(
    () =>
      media
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id),
    [media],
  );
  const viewerItem = sortedMedia[viewerIndex];

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminDarshan({ type, date: isoDateQuery });
      setMedia(data ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [type, isoDateQuery]);

  useEffect(() => {
    setAddDate(date);
  }, [date]);

  const prevDay = () => setDate((d) => d.subtract(1, "day"));
  const nextDay = () => setDate((d) => d.add(1, "day"));

  const canNext = useMemo(() => date.isBefore(dayjs(), "day"), [date]);
  const canPrev = useMemo(() => dayjs().diff(date, "day") < 45, [date]);

  const handleCreate = async () => {
    const payload: DarshanMediaCreate = {
      type,
      date: isoAddDateUtc,
      mediaType: form.mediaType,
      url: form.url,
      title: form.title?.trim() ? form.title : undefined,
    };
    const created = await createAdminDarshan(payload);
    if (created?.id) {
      setForm({ mediaType: "image", url: "", title: "" });
      await load();
    }
  };

  const handleDelete = async (id: number) => {
    await deleteAdminDarshan(id);
    await load();
  };

  const handleUpdateTitle = async (id: number, title: string) => {
    await updateAdminDarshan(id, { title });
    await load();
  };

  const handleFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    // Build queue
    const startItems: QueueItem[] = arr.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f,
      name: f.name,
      size: f.size,
      mediaType: f.type.startsWith("image/") ? "image" : f.type.startsWith("video/") ? "video" : form.mediaType,
      progress: 0,
      title: form.title || "",
      preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : undefined,
      uploading: true,
    }));
    setQueue((q) => [...startItems, ...q]);
    // If single, also support preview flow by setting form values when upload completes
    const single = startItems.length === 1;
    setUploading(true);
    try {
      for (let i = 0; i < startItems.length; i++) {
        const idx = i;
        const item = startItems[idx];
        try {
          const url = await uploadAdminMedia(item.file, `darshan/${isoAddDate}`, (p) => {
            setQueue((q) => q.map((qi) => (qi.id === item.id ? { ...qi, progress: p } : qi)));
            // overall indicator (legacy)
            setUploadProgress(Math.round(((idx * 100) + p) / startItems.length));
          });
          setQueue((q) => q.map((qi) => (qi.id === item.id ? { ...qi, url, progress: 100, uploading: false } : qi)));
          if (single) {
            setForm((f) => ({ ...f, url, mediaType: item.mediaType }));
          }
        } catch (err: any) {
          setQueue((q) =>
            q.map((qi) => (qi.id === item.id ? { ...qi, error: err?.message || "Upload failed", uploading: false } : qi)),
          );
        }
      }
      if (startItems.length > 1) {
        showSnackbar("Files uploaded. Review titles and click Add All to create.", "success");
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    await handleFiles(e.target.files);
    // reset input so same files can be reselected later
    e.target.value = "";
  };

  const moveItem = async (index: number, dir: -1 | 1) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= media.length) return;
    const arr = [...media];
    const [moved] = arr.splice(index, 1);
    arr.splice(newIndex, 0, moved);
    setMedia(arr);
    // persist sequential order (1-based)
    for (let i = 0; i < arr.length; i++) {
      const m = arr[i];
      try {
        await updateAdminDarshan(m.id, { order: i + 1 });
      } catch { }
    }
    // refresh afterwards
    await load();
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = async (index: number) => {
    if (dragIndex === null || dragIndex === index) return;
    const arr = [...media];
    const [moved] = arr.splice(dragIndex, 1);
    arr.splice(index, 0, moved);
    setMedia(arr);
    for (let i = 0; i < arr.length; i++) {
      const m = arr[i];
      try { await updateAdminDarshan(m.id, { order: i + 1 }); } catch { }
    }
    setDragIndex(null);
    await load();
  };

  // Admin lightbox keyboard nav
  useEffect(() => {
    if (!viewerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setViewerOpen(false);
      if (e.key === "ArrowLeft") setViewerIndex((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setViewerIndex((i) => Math.min(media.length - 1, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewerOpen, media.length]);

  const onDropFiles: React.DragEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault();
    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    await handleFiles(e.dataTransfer.files);
  };

  const removeFromQueue = (id: string) => {
    setQueue((q) => {
      const it = q.find((x) => x.id === id);
      if (it?.preview) URL.revokeObjectURL(it.preview);
      return q.filter((i) => i.id !== id);
    });
  };

  const addAllFromQueue = async () => {
    const toCreate = queue.filter((i) => i.url && !i.created);
    if (toCreate.length === 0) return;
    let createdCount = 0;
    let failed = 0;
    try {
      for (const item of toCreate) {
        try {
          setQueue((q) => q.map((qi) => (qi.id === item.id ? { ...qi, creating: true } : qi)));
          await createAdminDarshan({
            type,
            date: isoAddDateUtc,
            mediaType: item.mediaType,
            url: item.url!,
            title: item.title?.trim() || undefined,
          });
          createdCount++;
          setQueue((q) => q.map((qi) => (qi.id === item.id ? { ...qi, created: true, creating: false } : qi)));
        } catch (e: any) {
          failed++;
          setQueue((q) =>
            q.map((qi) => (qi.id === item.id ? { ...qi, creating: false, error: e?.message || "Create failed" } : qi)),
          );
        }
      }
      if (createdCount) showSnackbar(`Added ${createdCount} item(s).`, "success");
      if (failed) showSnackbar(`${failed} item(s) failed to add.`, "warning");
      await load();
      // Clear created items from queue
      setQueue((q) => q.filter((i) => !i.created));
    } catch (e) {
      showSnackbar("Failed to add some items.", "danger");
    }
  };

  const retryUpload = async (id: string) => {
    const item = queue.find((q) => q.id === id);
    if (!item) return;
    try {
      setQueue((q) => q.map((qi) => (qi.id === id ? { ...qi, error: undefined, uploading: true, progress: 0 } : qi)));
      const url = await uploadAdminMedia(item.file, `darshan/${isoAddDate}`, (p) => {
        setQueue((q) => q.map((qi) => (qi.id === id ? { ...qi, progress: p } : qi)));
      });
      setQueue((q) => q.map((qi) => (qi.id === id ? { ...qi, url, uploading: false, progress: 100 } : qi)));
      showSnackbar("Upload retried successfully.", "success");
    } catch (e: any) {
      setQueue((q) =>
        q.map((qi) => (qi.id === id ? { ...qi, error: e?.message || "Upload failed", uploading: false } : qi)),
      );
      showSnackbar("Upload retry failed.", "danger");
    }
  };

  const retryCreate = async (id: string) => {
    const item = queue.find((q) => q.id === id);
    if (!item || !item.url) return;
    try {
      setQueue((q) => q.map((qi) => (qi.id === id ? { ...qi, creating: true, error: undefined } : qi)));
      await createAdminDarshan({ type, date: isoAddDateUtc, mediaType: item.mediaType, url: item.url, title: item.title?.trim() || undefined });
      setQueue((q) => q.map((qi) => (qi.id === id ? { ...qi, creating: false, created: true } : qi)));
      showSnackbar("Item added.", "success");
      await load();
      setQueue((q) => q.filter((i) => !(i.id === id && i.created)));
    } catch (e: any) {
      setQueue((q) =>
        q.map((qi) => (qi.id === id ? { ...qi, creating: false, error: e?.message || "Create failed" } : qi)),
      );
      showSnackbar("Create retry failed.", "danger");
    }
  };

  const moveQueue = (index: number, dir: -1 | 1) => {
    const newIndex = index + dir;
    setQueue((q) => {
      if (newIndex < 0 || newIndex >= q.length) return q;
      const copy = [...q];
      const [m] = copy.splice(index, 1);
      copy.splice(newIndex, 0, m);
      return copy;
    });
  };

  const handleQueueDragStart = (index: number) => {
    setQueueDragIndex(index);
  };

  const handleQueueDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const pos = e.clientY < rect.top + rect.height / 2 ? "above" : "below";
    setQueueDragOverIndex(index);
    setQueueDragOverPos(pos);
  };

  const handleQueueDrop = (index: number) => {
    setQueue((q) => {
      if (queueDragIndex === null) return q;
      let to = index;
      if (queueDragOverPos === "below") to = index + 1;
      const copy = [...q];
      const [m] = copy.splice(queueDragIndex, 1);
      const insertIndex = to > queueDragIndex ? to - 1 : to;
      copy.splice(insertIndex, 0, m);
      return copy;
    });
    setQueueDragIndex(null);
    setQueueDragOverIndex(null);
    setQueueDragOverPos(null);
  };

  const handleQueueDragEnd = () => {
    setQueueDragIndex(null);
    setQueueDragOverIndex(null);
    setQueueDragOverPos(null);
  };

  return (
    <Box>
      <Stack spacing={3} sx={{ pb: 2 }}>
        <Card
          variant="outlined"
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "xl",
            borderColor: "divider",
            boxShadow: "lg",
            background: heroBackground,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: heroOverlay,
              opacity: 0.9,
              pointerEvents: "none",
            }}
          />
          <CardContent sx={{ position: "relative" }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
                <Stack spacing={0.75}>
                  <Chip size="sm" variant="soft" color="neutral" sx={{ alignSelf: "flex-start", fontWeight: 600 }}>
                    Daily darshan stewardship
                  </Chip>
                  <Typography level="h2" sx={{ fontWeight: 800, color: heroTitle }}>
                    Curate today's darshan offerings
                  </Typography>
                  <Typography level="body-sm" sx={{ maxWidth: 680, color: heroBody }}>
                    Guide the temple's daily darshan with warm copy, thoughtful sequencing, and uplifting messaging for devotees visiting the gallery.
                  </Typography>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    size="sm"
                    variant="soft"
                    color="neutral"
                    startDecorator={<RefreshRoundedIcon />}
                    onClick={() => load()}
                    loading={loading}
                  >
                    Refresh darshan
                  </Button>
                  <Button
                    size="sm"
                    variant="soft"
                    color="primary"
                    component={Link}
                    to="/darshan-gallery"
                    startDecorator={<PhotoLibraryRoundedIcon />}
                  >
                    Open public gallery
                  </Button>
                </Stack>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: "lg",
                    background: panelWarm,
                    boxShadow: "sm",
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: "text.secondary" }}>
                      Align the sequence
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      Keep darshan flowing
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                      Order images and videos to tell the day's devotional story.
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: "lg",
                    background: panelCool,
                    boxShadow: "sm",
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: "text.secondary" }}>
                      Share with devotees
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      Publish with warmth
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                      Add captions and details that uplift visitors in the gallery.
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Stack spacing={3}>
          <Sheet
            variant="outlined"
            sx={{
              borderRadius: "xl",
              p: { xs: 2.5, md: 3 },
              gap: 2,
              boxShadow: "0 18px 36px rgba(15,23,42,0.08)",
            }}
          >
            <Stack spacing={0.75}>
              <Typography level="title-md" sx={{ fontWeight: 700 }}>
                Select darshan focus
              </Typography>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Choose which darshan and day you are curating. You can step back up to 45 days or revisit yesterday’s celebration with a single tap.
              </Typography>
            </Stack>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ md: "flex-end" }}
              justifyContent="space-between"
              sx={{ flexWrap: "wrap" }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ flex: 1 }}>
                <FormControl sx={{ minWidth: { sm: 220 } }}>
                  <FormLabel>Darshan type</FormLabel>
                  <Select value={type} onChange={(_, v) => setType((v as DarshanType) ?? "Sringar")}>
                    {DARSHAN_TYPES.map((t) => (
                      <Option key={t} value={t}>
                        {t} Darshan
                      </Option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: { sm: 240 } }}>
                  <FormLabel>Darshan date</FormLabel>
                  <JoyDatePicker label={undefined as any} value={date} onChange={(d) => d && setDate(d)} />
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Button
                  size="sm"
                  variant="plain"
                  color="neutral"
                  startDecorator={<ChevronLeftRounded />}
                  disabled={!canPrev}
                  onClick={prevDay}
                >
                  Previous day
                </Button>
                <Button
                  size="sm"
                  variant="plain"
                  color="neutral"
                  endDecorator={<ChevronRightRounded />}
                  disabled={!canNext}
                  onClick={nextDay}
                >
                  Next day
                </Button>
              </Stack>
            </Stack>
            <Divider sx={{my: 2}}/>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip size="sm" variant="soft" color="primary">
                {sortedMedia.length} curated offering{sortedMedia.length === 1 ? "" : "s"}
              </Chip>
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                Viewing schedule for {isoDateDisplay}
              </Typography>
            </Stack>
          </Sheet>

          <Grid container spacing={3} alignItems="stretch">
            <Grid xs={12} lg={7}>
              <Sheet
                variant="plain"
                sx={{
                  ...gradient,
                  borderRadius: "xl",
                  p: { xs: 2.5, md: 3 },
                  position: "relative",
                  overflow: "hidden",
                  minHeight: "100%",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "radial-gradient(75% 95% at 90% 10%, rgba(255,255,255,0.28) 0%, transparent 65%), radial-gradient(85% 70% at -5% 105%, rgba(9,63,155,0.35) 0%, transparent 80%)",
                    pointerEvents: "none",
                  }}
                />
                <Stack spacing={2.5} sx={{ position: "relative", zIndex: 1 }}>
                  <Stack spacing={0.5}>
                    <Chip size="sm" variant="soft" color="neutral" sx={{ alignSelf: "flex-start", fontWeight: 600 }}>
                      New darshan upload
                    </Chip>
                    <Typography level="title-lg" sx={{color: gradient.muted, fontWeight: 700, pt: 2 }}>
                      Share today’s transcendental moments
                    </Typography>
                    <Typography level="body-sm" sx={{ color: gradient.muted, maxWidth: 520 }}>
                      Drag in freshly captured photos or videos, arrange their order, and add heartfelt titles before publishing them to the community gallery.
                    </Typography>
                  </Stack>
                  <FormControl>
                    <FormLabel sx={{ color: gradient.muted }}>Media files</FormLabel>
                    <Sheet
                      variant="soft"
                      color="neutral"
                      sx={{
                        borderRadius: "lg",
                        border: "1px dashed rgba(255,255,255,0.4)",
                        backgroundColor: "rgba(255,255,255,0.16)",
                        backdropFilter: "blur(16px)",
                        cursor: "pointer",
                      }}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={onDropFiles}
                    >
                      <DropZone />
                    </Sheet>
                    <input
                      ref={fileInputRef}
                      type="file"
                      style={{ display: "none" }}
                      accept={form.mediaType === "image" ? "image/*" : "video/*"}
                      multiple
                      onChange={handleFileChange}
                    />
                    {uploading ? (
                      <Typography level="body-xs" sx={{ mt: 0.75, color: gradient.muted }}>
                        Uploading… {uploadProgress}%
                      </Typography>
                    ) : (
                      <Typography level="body-xs" sx={{ mt: 0.75, color: gradient.muted }}>
                        Tip: you can drop multiple items, refine their titles, then add them all together.
                      </Typography>
                    )}
                  </FormControl>

                  {queue.length > 0 ? (
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography level="title-sm" sx={{ color: gradient.muted }}>
                          Upload queue
                        </Typography>
                        <Chip size="sm" variant="soft" color="neutral">
                          {queueReadyCount} ready / {queue.length} item{queue.length === 1 ? "" : "s"}
                        </Chip>
                      </Stack>
                      {queue.map((q, idx) => (
                        <Sheet
                          key={q.id}
                          variant="soft"
                          color="neutral"
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            gap: 1.5,
                            alignItems: { xs: "stretch", md: "center" },
                            borderRadius: "lg",
                            p: 1.5,
                            border: queueDragOverIndex === idx ? "2px solid var(--joy-palette-primary-outlinedBorder)" : "1px solid rgba(255,255,255,0.28)",
                            backgroundColor: "rgba(255,255,255,0.18)",
                          }}
                          draggable
                          onDragStart={() => handleQueueDragStart(idx)}
                          onDragOver={(e) => handleQueueDragOver(e, idx)}
                          onDrop={() => handleQueueDrop(idx)}
                          onDragEnd={handleQueueDragEnd}
                        >
                          {/* Thumbnail */}
                          <Box
                            sx={{
                              width: 72,
                              height: 72,
                              borderRadius: "md",
                              overflow: "hidden",
                              backgroundColor: "rgba(255,255,255,0.2)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {q.preview || q.url ? (
                              q.mediaType === "image" ? (
                                <img src={q.preview || q.url} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                <VideocamRoundedIcon />
                              )
                            ) : (
                              <VideocamRoundedIcon />
                            )}
                          </Box>

                          {/* Main content area */}
                          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                            {/* File info row */}
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }} sx={{ width: "100%" }}>
                              <IconButton
                                size="sm"
                                variant="plain"
                                color="neutral"
                                title="Drag to reorder"
                                sx={{ flexShrink: 0 }}
                              >
                                <DragIndicatorIcon />
                              </IconButton>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FileUpload
                                  fileName={q.name}
                                  fileSize={`${Math.round(q.size / 1024)} KB`}
                                  progress={q.progress}
                                  sx={{ width: "100%", minWidth: 0 }}
                                />
                              </Box>
                            </Stack>

                            {/* Title input */}
                            <Input
                              size="sm"
                              placeholder="Add title"
                              value={q.title}
                              onChange={(e) =>
                                setQueue((prev) => prev.map((it) => (it.id === q.id ? { ...it, title: e.target.value } : it)))
                              }
                              sx={{ width: "100%" }}
                            />

                            {/* Action buttons row */}
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start" justifyContent="space-between">
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <IconButton size="sm" variant="outlined" onClick={() => moveQueue(idx, -1)} title="Move up">
                                  <ArrowUpwardRoundedIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="sm" variant="outlined" onClick={() => moveQueue(idx, 1)} title="Move down">
                                  <ArrowDownwardRoundedIcon fontSize="small" />
                                </IconButton>
                              </Stack>

                              <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75} alignItems={{ xs: "stretch", sm: "center" }}>
                                {!q.url && q.error ? (
                                  <Button size="sm" variant="outlined" color="neutral" onClick={() => retryUpload(q.id)} startDecorator={<ReplayRoundedIcon />}>
                                    Retry upload
                                  </Button>
                                ) : null}
                                {q.url && !q.created && q.error ? (
                                  <Button size="sm" variant="outlined" color="neutral" onClick={() => retryCreate(q.id)} startDecorator={<ReplayRoundedIcon />}>
                                    Retry create
                                  </Button>
                                ) : null}
                                <Button size="sm" variant="plain" color="neutral" onClick={() => removeFromQueue(q.id)}>
                                  Remove
                                </Button>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Sheet>
                      ))}
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
                        <Button
                          variant="soft"
                          color="neutral"
                          startDecorator={<CloudUploadRoundedIcon />}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Add more files
                        </Button>
                        <Button
                          variant="solid"
                          color="primary"
                          startDecorator={<PlaylistAddRoundedIcon />}
                          onClick={addAllFromQueue}
                          disabled={queueReadyCount === 0}
                        >
                          Add all to darshan
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    form.url && (
                      <Sheet
                        variant="soft"
                        color="neutral"
                        sx={{
                          borderRadius: "lg",
                          p: 1.5,
                          backgroundColor: "rgba(255,255,255,0.16)",
                          backdropFilter: "blur(16px)",
                          maxWidth: 420,
                        }}
                      >
                        <Typography level="body-sm" sx={{ color: gradient.muted, mb: 1 }}>
                          Preview
                        </Typography>
                        <Box sx={{ borderRadius: "md", overflow: "hidden" }}>
                          {form.mediaType === "image" ? (
                            <img src={form.url} alt="preview" style={{ width: "100%", display: "block" }} />
                          ) : (
                            <video src={form.url} controls style={{ width: "100%", display: "block" }} />
                          )}
                        </Box>
                      </Sheet>
                    )
                  )}
                </Stack>
              </Sheet>
            </Grid>
            <Grid xs={12} lg={5}>
              <Sheet
                variant="outlined"
                sx={{
                  borderRadius: "xl",
                  p: { xs: 2.5, md: 3 },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Stack spacing={0.75}>
                  <Typography level="title-md" sx={{ fontWeight: 700 }}>
                    Publish details
                  </Typography>
                  <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                    Align the upload date, type, and devotional caption so devotees understand the mood of each darshan.
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <FormControl>
                    <FormLabel>Upload date</FormLabel>
                    <JoyDatePicker label={undefined as any} value={addDate} onChange={(d) => d && setAddDate(d)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Media type</FormLabel>
                    <Select
                      value={form.mediaType}
                      onChange={(_, v) => setForm((f) => ({ ...f, mediaType: (v as "image" | "video") ?? "image" }))}
                    >
                      <Option value="image">Image</Option>
                      <Option value="video">Video</Option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Title (optional)</FormLabel>
                    <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Sri Sri Radha Madhava morning darshan" />
                  </FormControl>
                </Stack>
                <Divider />
                <Stack spacing={1}>
                  <Button
                    size="md"
                    variant="solid"
                    color="primary"
                    startDecorator={<PlaylistAddRoundedIcon />}
                    onClick={handleCreate}
                    disabled={!form.url || queue.length > 0}
                  >
                    Add selected media
                  </Button>
                  <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                    Uploads join today’s darshan immediately. Finish any queue uploads or clear them before adding a single item.
                  </Typography>
                </Stack>
              </Sheet>
            </Grid>
          </Grid>

          <Sheet
            variant="outlined"
            sx={{
              borderRadius: "xl",
              p: { xs: 2.5, md: 3 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} justifyContent="space-between">
              <Stack spacing={0.5}>
                <Typography level="title-md" sx={{ fontWeight: 700 }}>
                  {type} darshan — {isoDateDisplay}
                </Typography>
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  Drag to reorder the sequence, refresh captions, or open the viewer to preview today’s darshan experience.
                </Typography>
              </Stack>
              <Chip size="sm" variant="soft" color="neutral">
                {sortedMedia.length} media item{sortedMedia.length === 1 ? "" : "s"}
              </Chip>
            </Stack>

            {loading ? (
              <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                <CircularProgress size="md" />
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  Gathering darshan records…
                </Typography>
              </Stack>
            ) : sortedMedia.length === 0 ? (
              <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ py: 6, textAlign: "center" }}>
                <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                  No darshan uploaded for this day yet
                </Typography>
                <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 420 }}>
                  Your first offering will illuminate the gallery with today’s darshan. Upload photos or video to share the temple’s beauty.
                </Typography>
                <Button
                  size="sm"
                  variant="soft"
                  color="primary"
                  startDecorator={<CloudUploadRoundedIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Start an upload
                </Button>
              </Stack>
            ) : (
              <>
                <Grid container spacing={2.5}>
                  {sortedMedia.map((m, idx) => {
                    const originalIndex = media.findIndex((item) => item.id === m.id);
                    return (
                      <Grid key={m.id} xs={12} sm={6} md={4} lg={3}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: "xl",
                            p: 1.25,
                            gap: 1,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            boxShadow: "0 14px 28px rgba(15,23,42,0.08)",
                          }}
                          draggable
                          onDragStart={() => handleDragStart(originalIndex)}
                          onDragOver={handleDragOver}
                          onDrop={() => handleDrop(originalIndex)}
                        >
                          <Box
                            sx={{ borderRadius: "lg", overflow: "hidden", cursor: "zoom-in", aspectRatio: "4 / 3" }}
                            onClick={() => {
                              setViewerIndex(idx);
                              setViewerOpen(true);
                            }}
                          >
                            {m.mediaType === "image" ? (
                              <img src={m.url} alt={m.title || "Darshan image"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                              <video src={m.url} controls style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            )}
                          </Box>
                          <Stack spacing={0.75} sx={{ flex: 1 }}>
                            <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                              {m.title || "Untitled darshan"}
                            </Typography>
                            <Input
                              size="sm"
                              placeholder="Update title"
                              value={m.title ?? ""}
                              onChange={(e) => handleUpdateTitle(m.id, e.target.value)}
                            />
                          </Stack>
                          <Stack direction="row" spacing={0.75} justifyContent="space-between" alignItems="center">
                            <IconButton size="sm" variant="plain" color="neutral" title="Drag to reorder">
                              <DragIndicatorIcon />
                            </IconButton>
                            <Stack direction="row" spacing={0.75}>
                              <IconButton size="sm" variant="outlined" onClick={() => moveItem(originalIndex, -1)}>
                                <ArrowUpwardRoundedIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="sm" variant="outlined" onClick={() => moveItem(originalIndex, 1)}>
                                <ArrowDownwardRoundedIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                            <Button size="sm" variant="plain" color="danger" onClick={() => handleDelete(m.id)}>
                              Delete
                            </Button>
                          </Stack>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
                <Modal open={viewerOpen} onClose={() => setViewerOpen(false)}>
                  <ModalDialog layout="fullscreen" variant="plain" sx={{ p: 0, bgcolor: "neutral.900" }}>
                    <IconButton
                      size="md"
                      variant="soft"
                      color="neutral"
                      onClick={() => setViewerOpen(false)}
                      sx={{ position: "fixed", top: 16, right: 16, zIndex: 2 }}
                    >
                      <CloseRoundedIcon />
                    </IconButton>
                    <IconButton
                      size="md"
                      variant="soft"
                      color="neutral"
                      onClick={() => setViewerIndex((i) => Math.max(0, i - 1))}
                      disabled={viewerIndex <= 0}
                      sx={{ position: "fixed", top: "50%", left: 16, transform: "translateY(-50%)", zIndex: 2 }}
                    >
                      <ChevronLeftRounded />
                    </IconButton>
                    <IconButton
                      size="md"
                      variant="soft"
                      color="neutral"
                      onClick={() => setViewerIndex((i) => Math.min(sortedMedia.length - 1, i + 1))}
                      disabled={viewerIndex >= sortedMedia.length - 1}
                      sx={{ position: "fixed", top: "50%", right: 16, transform: "translateY(-50%)", zIndex: 2 }}
                    >
                      <ChevronRightRounded />
                    </IconButton>
                    <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
                      {viewerItem?.mediaType === "image" ? (
                        <img src={viewerItem?.url} alt={viewerItem?.title || "Darshan"} style={{ maxHeight: "90vh", maxWidth: "100%", display: "block" }} />
                      ) : (
                        <video src={viewerItem?.url} controls style={{ maxHeight: "90vh", maxWidth: "100%", display: "block" }} />
                      )}
                    </Box>
                    {viewerItem?.title ? (
                      <Typography level="body-md" sx={{ position: "fixed", bottom: 16, left: 16, color: "#fff" }}>
                        {viewerItem.title}
                      </Typography>
                    ) : null}
                  </ModalDialog>
                </Modal>
              </>
            )}
          </Sheet>
        </Stack>
      </Stack>
    </Box>
  );
}
